/**
 * Sign-in.
 *
 * The error message is deliberately the same for an unknown email and a wrong
 * password — the API does not distinguish them either (ADR 0004), because
 * telling an attacker which half was right turns a login form into an account
 * enumeration tool.
 *
 * The split panel is the reference prototype's (task 6.5.7): navy brand side,
 * white form side. It is the one screen a user sees before they are anybody, so
 * it carries the logo and the product's one-sentence claim — and it is where
 * the design system's navy share is most visible.
 *
 * Below 920px the two panels stack, brand above form, rather than dropping the
 * brand side: on a phone the logo is the only confirmation the user is signing
 * in to the right system.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 5.13, 6.5.7
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import { STAGE_RAMP } from '../../../components/domain/stages';
import { Button } from '../../../components/ui/Button';
import { GvLogo } from '../../../components/ui/GvLogo';
import { useAuth } from '../../../lib/auth-context';

interface LocationState {
  from?: string;
}

/** The prototype's field treatment: an uppercase display-font label. */
const FIELD_LABEL =
  'mb-1.5 block font-display text-[11px] font-extrabold tracking-[0.1em] text-body uppercase';

const FIELD_INPUT =
  'border-gray-200 focus:border-blue text-navy-text min-h-11 w-full rounded-md ' +
  'border-[1.5px] px-3.5 focus:outline-none';

export function LoginPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { user, isLoading, signIn } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) return <p>{t('state.loading')}</p>;

  /* Already signed in: back to wherever they were headed. */
  if (user !== null) {
    const from = (location.state as LocationState | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  /* `SyntheticEvent`, not the deprecated `FormEvent` — React 19's types note
     that FormEvent never corresponded to a real DOM event. */
  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    void signIn({ email, password })
      .catch(() => {
        /* One message for both failure modes — see the note above. */
        setError(t('auth.invalidCredentials'));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="grid min-h-dvh grid-cols-1 min-[920px]:grid-cols-[1.15fr_1fr]">
      {/* Brand side. */}
      <div className="bg-navy px-s4 py-s5 min-[920px]:px-16 min-[920px]:py-14 flex flex-col justify-between text-white">
        <GvLogo on="navy" height={38} clearSpace={false} />

        <div className="py-s5 max-w-[520px]">
          <h1 className="mb-s3 text-[32px] leading-[1.05] tracking-[-0.01em] text-white min-[920px]:text-[42.7px]">
            {/* Split so the closing phrase can take the green accent without
                the translation carrying markup. */}
            {t('auth.pitchLead')}{' '}
            <span className="text-green">{t('auth.pitchAccent')}</span>
          </h1>
          <p className="text-gray-200 text-[14.7px] leading-relaxed font-light">
            {t('auth.pitchBody')}
          </p>

          {/* The funnel ramp, green → navy. Decorative: it repeats the pitch
              above rather than adding to it, so it is not announced. */}
          <div className="gap-[3px] mt-s5 flex" aria-hidden="true">
            {STAGE_RAMP.map((color) => (
              <i
                key={color}
                className="h-1.5 flex-1 rounded-full"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        {/* gray-200, not gray-400: this sits on the navy panel, where the
            darker neutral drops below AA. gray-200 is the sibling already used
            for the pitch body above it. */}
        <p className="pt-s4 text-xs text-gray-200">{t('app.tagline')}</p>
      </div>

      {/* Form side. */}
      <main className="px-s4 py-s5 grid place-items-center bg-white">
        <div className="w-full max-w-[360px]">
          <h2 className="mb-s1 text-[26.7px]">{t('auth.signInTitle')}</h2>
          <p className="text-body mb-s4 text-[13.5px] font-light">
            {t('auth.signInLead')}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-s3">
            <div>
              <label htmlFor="login-email" className={FIELD_LABEL}>
                {t('auth.email')}
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                required
                autoComplete="username"
                className={FIELD_INPUT}
              />
            </div>

            <div>
              <label htmlFor="login-password" className={FIELD_LABEL}>
                {t('auth.password')}
              </label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                required
                autoComplete="current-password"
                className={FIELD_INPUT}
              />
            </div>

            {/* `role="alert"` so the failure is announced, not only shown. */}
            {error !== null ? (
              <p role="alert" className="text-sem-bad text-sm">
                {error}
              </p>
            ) : null}

            <Button type="submit" size="lg" disabled={submitting}>
              {submitting ? t('state.loading') : t('auth.signIn')}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
